import { type ColumnDef } from '@tanstack/react-table';
import { StatusIndicator } from '@/components/data-table/data-table-status-indicator';
import { ColumnType } from '@/types/column';
import { type GalleryView } from '@/types';

interface StatusColumnOptions<T> {
  id: string;
  accessorFn: (row: T) => string;
  accessorKey: keyof T;
  label: string;
  enableSorting?: boolean;
  enableHiding?: boolean;
  minSize?: number;
  maxSize?: number;
  size?: number;
  cellClassName?: string;
  headerClassName?: string;
  galleryView?: GalleryView;
  dataCy?: string;
}

export const StatusColumn = <T extends Record<string, any>>(opts: StatusColumnOptions<T>): ColumnDef<T> => {
  const {
    id,
    accessorFn,
    accessorKey = ColumnType.Status as keyof T,
    size = 100,
    label,
    cellClassName,
    headerClassName,
    enableSorting = false,
    enableHiding = true,
    minSize = 100,
    maxSize = 100,
    galleryView,
    dataCy,
  } = opts;

  return {
    id,
    enableSorting,
    enableHiding,
    minSize,
    maxSize,
    accessorFn,
    accessorKey,
    header: () => label,
    ...((cellClassName ?? headerClassName) && {
      meta: {
        ...(cellClassName && { cellClassName: cellClassName }),
        ...(headerClassName && { headerClassName: headerClassName }),
      },
    }),
    size,
    cell: ({ row }) => (
      <StatusIndicator
        row={row}
        value={accessorKey as string}
        data-cy={dataCy ?? `${galleryView ?? 'list'}-${row.index}-${id}`}
      />
    ),
  };
};
